import { ThreeEvent, useThree } from "@react-three/fiber";
import { useCallback, useEffect, useState } from "react";
import { useRef } from "react";
import { FC } from "react";
import { DataTexture, DoubleSide, Mesh } from "three";
import {
  IRShellMaterialProps,
  IRShellMeshData,
} from "../../types/laserScanScene";
import { useBVH } from "@react-three/drei";
import {
  convertFahrenheitToUserPreference,
  getUnit,
} from "~/helpers/userPreference";
import { UNITS } from "~/constants/unit";
import { useLaserScanScene } from "../../providers/LaserScanSceneProvider";
import { useLaserSceneWorker } from "../../hooks/useLaserSceneWorker";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

interface IRShellMeshProps {
  canvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
  tooltipRef: React.MutableRefObject<HTMLSpanElement | null>;
  isExpanded: boolean;
  clearTooltip: () => void;
}

const IRShellMesh: FC<IRShellMeshProps> = ({
  canvasRef,
  tooltipRef,
  isExpanded,
  clearTooltip,
}) => {
  const meshRef = useRef<Mesh>();

  const [shellData, setShellData] = useState<IRShellMeshData[]>([]);

  const { camera, get } = useThree();

  const { heatData, isFirstRenderRef } = useLaserScanScene();

  const { calculateIRShell } = useLaserSceneWorker();

  useBVH(meshRef);

  useEffect(() => {
    if (!heatData?.shellData) return;
    calculateIRShell(heatData.shellData).then((result) => {
      setShellData(result || []);
      const control = get().controls as OrbitControls;
      if (control && isFirstRenderRef.current) {
        camera.position.set(0, -18, 8);
        control.target.set(0, -6, -2);
        control.update();
        isFirstRenderRef.current = false;
      }
    });
    //INFO, Fatih: shellData is huge data, dont use in dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heatData?.shellData?.east?.Id, calculateIRShell]);

  const hoverHandler = (event: ThreeEvent<PointerEvent>) => {
    if (!event.intersections || event.intersections.length === 0) {
      return;
    }
    const intersection = event.intersections[0];

    const isShellIntersection = intersection.object.userData.isShell;

    if (isShellIntersection) {
      const point = intersection.point;
      if (!point) {
        clearTooltip();
        return;
      }

      const uvX = intersection.uv?.x;
      const uvY = intersection.uv?.y;

      const clock = intersection.object.name;

      const currentShellData = shellData?.find((x) => x.key === clock)
        ?.materialProps?.map;

      if (!currentShellData || !uvX || !uvY) return;

      const rowVal = Math.floor(uvX * currentShellData.width);
      const colVal =
        currentShellData.height - 1 - Math.floor(uvY * currentShellData.height);

      const itemOrder = colVal * currentShellData.width + rowVal;

      const itemVal =
        currentShellData.temperatureData[itemOrder] *
        (currentShellData.calibrationFactor || 1);

      const temperature = isNaN(itemVal)
        ? "N/A"
        : convertFahrenheitToUserPreference(+itemVal).toFixed(0) +
          "" +
          getUnit(UNITS.CELSIUS);

      requestAnimationFrame(() => {
        if (tooltipRef.current && canvasRef.current) {
          canvasRef.current.style.cursor = "crosshair";
          tooltipRef.current.style.display = "unset";
          tooltipRef.current.style.left = `${event.offsetX - 25}px`;
          tooltipRef.current.style.top = `${
            event.offsetY - (isExpanded ? 75 : 65)
          }px`;
          tooltipRef.current.innerHTML = `<div>${temperature}</div><div style="font-size: 14px">Clock: ${clock}</div>`;
        }
      });
    }
  };
  const getTexture = useCallback((materialProps: IRShellMaterialProps) => {
    const text = new DataTexture(
      materialProps.map.data,
      materialProps.map.width,
      materialProps.map.height,
      materialProps.map.format,
    );
    text.needsUpdate = true;
    text.flipY = materialProps.map.flipY;
    return text;
  }, []);

  return (
    <>
      {shellData?.map(({ geometryProps, materialProps, meshProps, key }) => (
        <mesh
          ref={(mesh) => (meshRef.current = mesh || undefined)}
          position={meshProps.position}
          rotation={meshProps.rotation}
          name={key}
          onPointerMove={hoverHandler}
          onPointerLeave={clearTooltip}
          onPointerOut={clearTooltip}
          raycast={Mesh.prototype.raycast}
          userData={{
            isShell: true,
          }}
          key={key}
        >
          <cylinderGeometry args={geometryProps.args} />
          <meshStandardMaterial
            side={DoubleSide}
            transparent={materialProps.transparent}
            opacity={materialProps.opacity}
            map={getTexture(materialProps)}
          />
        </mesh>
      ))}
    </>
  );
};

export default IRShellMesh;
